import { useState } from "react";

// @mui material components
import Container from "@mui/material/Container";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

// Data
import reviewsData, { featuredIndices } from "pages/Home/sections/data/reviewsData";
import propertyData from "pages/Home/sections/data/propertyData";

function truncate(text, max) {
  if (text.length <= max) return text;
  return text.substring(0, max).replace(/\s+\S*$/, "") + "…";
}

const marqueeKeyframes = {
  "@keyframes marquee": {
    "0%": { transform: "translateX(0)" },
    "100%": { transform: "translateX(-50%)" },
  },
};

function ReviewCard({ review }) {
  return (
    <MKBox
      sx={{
        minWidth: { xs: 300, md: 380 },
        maxWidth: { xs: 300, md: 380 },
        flex: "0 0 auto",
      }}
    >
      <MKBox
        bgColor="white"
        borderRadius="xl"
        shadow="lg"
        p={3}
        sx={{
          height: "100%",
          border: "1px solid",
          borderColor: "grey.200",
          position: "relative",
        }}
      >
        {/* Airbnb badge */}
        <MKTypography
          variant="caption"
          sx={{
            position: "absolute",
            top: 12,
            right: 14,
            color: "#FF5A5F",
            fontWeight: 999,
            fontSize: "0.75rem",
            letterSpacing: 0.5,
          }}
        >
          Airbnb
        </MKTypography>

        {/* Name & date */}
        <MKBox display="flex" alignItems="center" gap={1} mb={1}>
          <Icon sx={{ color: "info.main" }}>person</Icon>
          <MKBox>
            <MKTypography variant="button" fontWeight="bold" color="dark">
              {review.name}
            </MKTypography>
            <MKTypography variant="caption" color="text" display="block">
              {review.date}
            </MKTypography>
          </MKBox>
        </MKBox>

        {/* Stars */}
        <MKBox display="flex" mb={1.5}>
          {Array.from({ length: 5 }, (_, s) => (
            <Icon key={s} sx={{ fontSize: 18, color: s < review.rating ? "#FFD700" : "#ccc" }}>
              star
            </Icon>
          ))}
        </MKBox>

        {/* Review text */}
        <MKTypography variant="body2" color="dark" sx={{ lineHeight: 1.6 }}>
          &ldquo;{truncate(review.review, 220)}&rdquo;
        </MKTypography>
      </MKBox>
    </MKBox>
  );
}

function Reviews() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { rating, reviews: reviewCount } = propertyData.highlights;

  const featured = featuredIndices.map((i) => reviewsData[i]);
  // Duplicate for seamless loop
  const scrollReviews = [...featured, ...featured];

  return (
    <MKBox component="section" py={6} id="reviews">
      <Container>
        <MKTypography variant="h2" textAlign="center">
          Guest Reviews
        </MKTypography>
        <MKBox display="flex" alignItems="center" justifyContent="center" width="100%" mt={1}>
          <Icon sx={{ mr: 0.5, color: "#FFD700" }}>star</Icon>
          <MKTypography variant="h4" color="info" textGradient>
            {rating} / 5
          </MKTypography>
          <MKTypography variant="body1" color="text" ml={1}>
            ({reviewCount} reviews)
          </MKTypography>
        </MKBox>
        <MKTypography variant="body2" color="text" textAlign="center" mt={1} mb={4}>
          Reviews from our{" "}
          <MKTypography
            component="a"
            href="https://www.airbnb.com/rooms/9803481"
            target="_blank"
            rel="noreferrer"
            variant="body2"
            color="info"
            fontWeight="bold"
          >
            Airbnb guests
          </MKTypography>
        </MKTypography>
      </Container>

      {/* Scrolling carousel */}
      <MKBox
        sx={{
          overflow: "hidden",
          width: "80%",
          mx: "auto",
          ...marqueeKeyframes,
        }}
      >
        <MKBox
          display="flex"
          gap={3}
          sx={{
            animation: "marquee 90s linear infinite",
            "&:hover": { animationPlayState: "paused" },
            width: "max-content",
          }}
        >
          {scrollReviews.map((review, i) => (
            <ReviewCard key={i} review={review} />
          ))}
        </MKBox>
      </MKBox>

      <MKBox textAlign="center" mt={4}>
        <MKButton variant="outlined" color="info" onClick={() => setDialogOpen(true)}>
          <Icon sx={{ mr: 1 }}>reviews</Icon>
          See All {reviewsData.length} Reviews
        </MKButton>
      </MKBox>

      {/* All Reviews Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <MKBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ color: "#FFD700" }}>star</Icon>
            <MKTypography variant="h5">
              {rating} / 5 — {reviewsData.length} Reviews
            </MKTypography>
          </MKBox>
        </DialogTitle>
        <DialogContent dividers>
          {reviewsData.map((review, i) => (
            <MKBox key={i}>
              {i > 0 && <Divider sx={{ my: 2 }} />}
              <MKBox display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
                <MKTypography variant="h6">{review.name}</MKTypography>
                <MKTypography variant="caption" color="text">
                  {review.date}
                </MKTypography>
              </MKBox>
              {review.location && (
                <MKTypography variant="caption" color="text" display="block" mb={0.5}>
                  {review.location}
                </MKTypography>
              )}
              <MKBox display="flex" mb={1}>
                {Array.from({ length: 5 }, (_, s) => (
                  <Icon
                    key={s}
                    sx={{ fontSize: 16, color: s < review.rating ? "#FFD700" : "#ccc" }}
                  >
                    star
                  </Icon>
                ))}
              </MKBox>
              <MKTypography variant="body2" color="text">
                {review.review}
              </MKTypography>
            </MKBox>
          ))}
        </DialogContent>
        <DialogActions>
          <MKButton variant="gradient" color="info" onClick={() => setDialogOpen(false)}>
            Close
          </MKButton>
        </DialogActions>
      </Dialog>
    </MKBox>
  );
}

import PropTypes from "prop-types";

ReviewCard.propTypes = {
  review: PropTypes.object.isRequired,
};

export default Reviews;
